/**
 * Markers Plugin for Search Results Map Variant
 *
 * Syncs search result data from the Redux store into the map marker facade and
 * handles marker click behavior.
 *
 * Single responsibility: keep the visual marker layer in sync with the current
 * search results, and open the listing URL when a marker is clicked on desktop.
 *
 * This plugin does NOT:
 * - Create or configure the map instance (see map.plugin)
 * - Handle KML boundary layers (see mapBoundaries.plugin)
 * - Manage site plan overlays (see sitePlan.plugin, interactiveSitePlan.plugin)
 *
 * Reason to change: how results are reflected as markers, how marker visibility
 * or mode (pin vs price) is resolved, or how marker clicks are handled.
 *
 * @module searchResultsMap/plugins/markers.plugin
 */

import { distinctUntilChanged, filter, map } from 'rxjs';
import { LocationType, selectLocationType } from 'features/searchResultsFacets';
import fetchMarkersResultsEpic from '../redux/fetchMarkersResultsEpic';
import { resolveMode, isBasicMarkerVisible } from '../services/markersBehavior';
import { areMapBoundariesDefined, selectMapResults } from '../redux/selectors';
import createMarkerClickHandler from '../handlers/markerClickHandler';

/**
 * Wires search result data into the map marker facade.
 *
 * Behavior summary:
 * - Paid results can switch to price pins once zoomed in enough.
 * - Basic results stay hidden until the user zooms in from the initial baseline.
 */
export default function createMarkersPlugin() {
  const state = {
    started: false,
    mapFacade: null,
    store$: null,
    getState: null,
    isDesktopBreakpoint: null,
    dispatch: null,
    subscriptions: [],
    isMarketOrCity: false,
    paidCount: 0,
  };

  function resolveVisibility(markerData, context) {
    if (!markerData.isBasic) {
      return context.defaultVisibility;
    }

    return isBasicMarkerVisible(
      context.zoom,
      context.defaultVisibility,
      { isMarketOrCity: state.isMarketOrCity, paidCount: state.paidCount },
    );
  }

  function subscribeToMarkerResults() {
    if (!state.mapFacade) {
      return;
    }

    state.subscriptions.push(
      state.store$.pipe(
        map((currentState) => ({
          results: selectMapResults(currentState),
          boundsDefined: areMapBoundariesDefined(currentState),
          locationType: selectLocationType(currentState),
        })),
        distinctUntilChanged(
          (a, b) =>
            a.results === b.results &&
            a.boundsDefined === b.boundsDefined &&
            a.locationType === b.locationType
        ),
        filter(({ boundsDefined }) => boundsDefined),
      ).subscribe(({ results, locationType }) => {
        state.isMarketOrCity =
          locationType === LocationType.Market.toString() ||
          locationType === LocationType.City.toString();
        state.paidCount = (results ?? []).filter((result) => !result.isBasic).length;
        state.mapFacade.syncMarkers(results);
      })
    );
  }

  return {
    name: 'search-results-markers',

    async init(context) {
      context.services.epic$.next(fetchMarkersResultsEpic);

      context.state.searchResultsMarkers = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.dispatch = context.services.dispatch;
      state.getState = context.services.getState;
      state.isDesktopBreakpoint = context.services.isDesktopBreakpoint;

      if (!state.mapFacade?.setMarkerBehavior) {
        return;
      }

      state.mapFacade.setMarkerBehavior({
        resolveMode,
        resolveVisibility,
        onClick: createMarkerClickHandler(state.dispatch, () => state.isDesktopBreakpoint?.() ?? false, state.getState),
      });

      subscribeToMarkerResults();

      state.started = true;
      context.state.searchResultsMarkers = {
        started: true,
      };
    },

    async stop(context) {
      state.mapFacade?.clearMarkerBehavior?.();
      state.mapFacade?.clearMarkers?.();

      state.subscriptions.forEach((subscription) => subscription.unsubscribe());
      state.subscriptions = [];

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.getState = null;
      state.isDesktopBreakpoint = null;
      state.dispatch = null;
      state.isMarketOrCity = false;
      state.paidCount = 0;

      delete context.state.searchResultsMarkers;
    },
  };
}
